/**
 * CommentBubble — single comment rendered inside the report detail.
 *
 * USER comments float right (the user's own follow-ups). MCP / STAFF /
 * SYSTEM float left and pick up an author label so you can tell at a
 * glance who wrote what. The "Mhosaic Team" label for MCP comments
 * matches PNR's pattern; hosts can hide it via a project flag (future).
 */

import { h } from 'preact'

import type { WidgetCommentRow } from '../types'
import type { StringKey } from './i18n'

interface CommentBubbleProps {
  comment: WidgetCommentRow
  strings: Record<StringKey, string>
}

export function CommentBubble({ comment, strings }: CommentBubbleProps) {
  // The submitter's own follow-ups float right; everything else (operator,
  // agent, system) floats left with an attribution label. Both submitter
  // and operator comments arrive as `author_source = USER` from the
  // backend, so we rely on the server-computed `is_mine` flag to tell
  // them apart instead of guessing from the source enum.
  const isMine = comment.is_mine
  const isAgent = !isMine && comment.author_source === 'mcp'
  const isSystem = !isMine && comment.author_source === 'system'
  const variant: 'mcp' | 'system' | 'staff' = isAgent
    ? 'mcp'
    : isSystem
      ? 'system'
      : 'staff'
  const labelKey: StringKey =
    variant === 'mcp'
      ? 'detail.author.mcp'
      : variant === 'system'
        ? 'detail.author.system'
        : 'detail.author.staff'
  const label = comment.author_label || strings[labelKey]
  return (
    <div class={`comment-bubble ${isMine ? 'is-mine' : 'is-other'}`}>
      {!isMine && label && (
        <div class={`comment-author comment-author--${variant}`}>{label}</div>
      )}
      <div class="comment-body">{comment.body}</div>
      <div class="comment-time">{formatTime(comment.created_at)}</div>
    </div>
  )
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}
